'use client';

import { Alert } from '@vs-shop/ui/alert';
import { Button } from '@vs-shop/ui/button';
import { Input } from '@vs-shop/ui/input';
import { captureCause } from '../../shared/utils/sentry';
import { Exit } from 'effect';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { getDefaultLocale } from '../../config/locale';

import { Notice } from '@vs-shop/ui/notice';

import effectTsResolver from '@vs-shop/engine/adapter/effect-hook-form/effectTsResolver';
import { resetPasswordCustomerAtomFn } from '@vs-shop/engine/atom/auth/mutation/resetPasswordCustomer';
import type { ResetPasswordFormData } from '@vs-shop/schemas/authForms';
import { ResetPasswordFormSchema } from '@vs-shop/schemas/authForms';
import { useAtomSet } from '@effect/atom-react';

type Props = {
  onSuccess?: () => void;
};

export default function ResetPasswordForm({ onSuccess: _onSuccess }: Props) {
  const intl = useIntl();
  const locale = getDefaultLocale();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [isPending, startTransition] = useTransition();
  const resetPasswordMutation = useAtomSet(resetPasswordCustomerAtomFn, { mode: 'promiseExit' });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: effectTsResolver(ResetPasswordFormSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data: { email: string }) => {
    setErrorMessage(null);

    startTransition(async () => {
      const result = await resetPasswordMutation({
        email: data.email,
        localeCode: locale,
      });

      Exit.match(result, {
        onFailure: (cause) => {
          captureCause(cause, {
            component: 'ResetPasswordForm',
            action: 'Reset password request failed',
            additionalData: {
              email: data.email,
            },
          });

          setErrorMessage(
            intl.formatMessage({
              id: 'component.resetPasswordForm.apiError',
              defaultMessage: 'Došlo k chybě při odesílání žádosti o resetování hesla',
            })
          );
        },
        onSuccess: (_response) => {
          setIsSubmitted(true);
        },
      });
    });
  };

  if (isSubmitted) {
    return (
      <div className="space-y-4 text-center">
        <Notice>
          {intl.formatMessage({
            id: 'component.resetPasswordForm.successMessage',
            defaultMessage: 'Pokyny pro resetování hesla byly odeslány na váš email.',
          })}
        </Notice>
      </div>
    );
  }

  return (
    <div>
      {errorMessage && (
        <Alert variant="destructive" className="mb-3" role="alert">
          {errorMessage}
        </Alert>
      )}

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          {...register('email')}
          id="email"
          type="email"
          label={intl.formatMessage({
            id: 'component.resetPasswordForm.email.label',
            defaultMessage: 'Email',
          })}
          placeholder={intl.formatMessage({
            id: 'component.resetPasswordForm.email.placeholder',
            defaultMessage: 'Emailová adresa',
          })}
          error={errors.email?.message ?? null}
          isTouched={false}
        />

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={isPending}
          disabled={isPending}
        >
          {intl.formatMessage({
            id: 'component.resetPasswordForm.button.submit',
            defaultMessage: 'RESETOVAT HESLO',
          })}
        </Button>
      </form>
    </div>
  );
}
